// components/UserDirectory/UserDirectory.tsx
// Read-only admin search over the flat profiles/{profileId} index, filtered
// by school. friendCount, schoolNameLower, and guardianAccountIds all come
// straight from that index - none of them are recomputed or written here.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { searchProfilesBySchool } from '../../services/userDirectoryService';
import type { FlatProfileDoc } from '../../types/flatProfile';
import './UserDirectory.css';

export const UserDirectory: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [schoolQuery, setSchoolQuery] = useState('');
    const [results, setResults] = useState<FlatProfileDoc[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const runSearch = async () => {
        if (!schoolQuery.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const profiles = await searchProfilesBySchool(schoolQuery);
            setResults(profiles);
            setHasSearched(true);
        } catch (err: any) {
            setError(err.message || 'Search failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-directory-container">
            <header className="user-directory-header">
                <button className="back-button" onClick={() => navigate('/dashboard')}>← Dashboard</button>
                <h1>User Directory</h1>
                <span className="current-user">{currentUser?.email}</span>
            </header>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="user-directory-search">
                <label htmlFor="school-search">Search by school</label>
                <div className="search-row">
                    <input
                        id="school-search"
                        type="text"
                        placeholder="e.g. Lincoln Elementary"
                        value={schoolQuery}
                        onChange={(e) => setSchoolQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                        className="search-input"
                    />
                    <button className="btn btn-primary" onClick={runSearch} disabled={loading || !schoolQuery.trim()}>
                        {loading ? 'Searching…' : 'Search'}
                    </button>
                </div>
                <p className="search-hint">
                    Matches profiles/{'{profileId}'}.schoolNameLower (Phase 4 flat index) - not a live
                    lowercase comparison against school.name.
                </p>
            </div>

            {hasSearched && !loading && (
                <div className="user-directory-results">
                    <h2>{results.length} profile{results.length === 1 ? '' : 's'} found</h2>
                    {results.length === 0 ? (
                        <p className="empty-state">No profiles match that school name.</p>
                    ) : (
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th>Explorer</th>
                                    <th>Age Bracket</th>
                                    <th>Status</th>
                                    <th>Friends</th>
                                    <th>Guardians</th>
                                    <th>Discoverable</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((profile) => (
                                    <tr key={profile.profileId}>
                                        <td>{profile.explorerName}</td>
                                        <td>{profile.ageBracket}</td>
                                        <td>{profile.status}</td>
                                        <td>{profile.friendCount ?? '—'}</td>
                                        <td>{profile.guardianAccountIds?.length ?? 0}</td>
                                        <td>{profile.discoverable ? 'Yes' : 'No'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};
